/**
 * The capacity concepts, rendered separately and labelled (A7Y-2821, epic §7).
 * One component, used by both the lease panel and the quota panel, so the two
 * surfaces cannot drift into describing capacity differently.
 *
 * WHY EACH ONE GETS ITS OWN ROW. "We stopped" has two different causes and
 * only one of them means we are out of quota:
 *
 *   PROVIDER HARD LIMIT      the provider refused — 100% of the window is spent.
 *   CDID OPERATING CEILING   OUR ceiling refused. The provider still has room;
 *                            we declined to use it because a session started
 *                            above this point buys a partial result and a handoff.
 *
 * Collapsing these into one "limit" is how an operator concludes "we're out of
 * quota" when the truth is "our own ceiling refused it" — the exact confusion
 * this ticket exists to remove. So each one gets its own labelled row, always,
 * even when only one of them is binding.
 *
 * A7Y-3699 — there is no third "currently reserved" row. Admission no longer
 * sizes a per-holder envelope; a run is admitted when both windows sit below
 * the ceiling on the freshly-probed reading, nothing more.
 *
 * NO FALLBACK NUMBERS. Every figure comes from the payload, which derives from
 * `POLICY`. An absent figure renders as an explicit unknown; a hardcoded default
 * boundary here would be a second admission regime wearing a fallback.
 */
import type { AutomationCapacity, LimitingResourceKind } from '@a7y/types';

const WINDOW_LABELS: Record<string, string> = {
  five_hour: '5h',
  weekly: 'weekly',
};

/**
 * How each refusal presents. The headline is the whole point: it must be
 * impossible to read an operating-ceiling or reserved-capacity stop as provider
 * exhaustion, so only `provider_hard_limit` is allowed to say we are out of
 * quota, and only it is red.
 */
const KIND_META: Record<LimitingResourceKind, { headline: string; detail: string; box: string }> = {
  provider_hard_limit: {
    headline: 'Provider hard limit reached',
    detail: 'The provider itself refused — this window is spent.',
    box: 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300',
  },
  operating_ceiling: {
    headline: 'Our operating ceiling refused this',
    detail:
      'CDID declined this, not the provider. Provider headroom remains — we stop here so a session does not end in a partial result and a handoff.',
    box: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  },
  reserved_capacity: {
    headline: 'Capacity reserved by live work',
    detail:
      'Nothing is exhausted and no ceiling was crossed: the room is set aside by work already running, and returns when it finishes.',
    box: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  },
};

const Unknown = ({ children }: { children: React.ReactNode }) => (
  <span className="text-zinc-400">{children}</span>
);

function ConceptRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="shrink-0 text-zinc-500">{label}</span>
      <span className="min-w-0 text-right tabular-nums text-zinc-800 dark:text-zinc-200">
        {children}
      </span>
    </div>
  );
}

function joinWindows(
  windows: AutomationCapacity['windows'],
  read: (window: AutomationCapacity['windows'][number]) => string | null
): React.ReactNode {
  const parts = windows
    .map((window) => {
      const value = read(window);
      return value === null ? null : `${WINDOW_LABELS[window.window] ?? window.window} ${value}`;
    })
    .filter((part): part is string => part !== null);
  if (parts.length === 0) return <Unknown>not stated</Unknown>;
  return parts.join(' · ');
}

export function CapacityConcepts({ capacity }: { capacity: AutomationCapacity }) {
  const limit = capacity.limitingResource;
  const kind = limit ? KIND_META[limit.capacityKind] : null;

  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-wide text-zinc-400">Capacity check</div>

      <ConceptRow label="Provider hard limit">
        {capacity.providerHardLimitPct != null ? (
          `${capacity.providerHardLimitPct}% of each window`
        ) : (
          <Unknown>not stated</Unknown>
        )}
      </ConceptRow>

      <ConceptRow label="CDID operating ceiling">
        {joinWindows(capacity.windows, (window) =>
          window.operatingCeilingPct == null ? null : `${window.operatingCeilingPct}%`
        )}
      </ConceptRow>

      <div className="flex items-baseline justify-between gap-3 text-[11px]">
        <span className="shrink-0 text-zinc-400">Last reading</span>
        <span className="min-w-0 text-right tabular-nums text-zinc-500">
          {joinWindows(capacity.windows, (window) =>
            // Null is not zero. An unread window is unknown, and drawing it as
            // 0% would paint an empty account.
            window.observedPct == null ? null : `${window.observedPct}%`
          )}
        </span>
      </div>

      <div className="text-[10px] leading-relaxed text-zinc-400">
        Admits when both windows sit below the ceiling on a fresh probe. Live admission probes
        again before a run starts.
      </div>

      {limit && kind ? (
        <div className={`mt-1 rounded-md px-2 py-1.5 text-[11px] leading-relaxed ${kind.box}`}>
          <div className="font-semibold">{kind.headline}</div>
          <div className="mt-0.5">{kind.detail}</div>
          {/* The comparator's own sentence, verbatim — never re-worded into
              exhaustion language. */}
          <div className="mt-1 font-mono text-[10px] opacity-80">{limit.message}</div>
        </div>
      ) : capacity.notEvaluatedReason ? (
        <div className="mt-1 rounded-md bg-zinc-50 px-2 py-1.5 text-[11px] leading-relaxed text-zinc-500 dark:bg-zinc-950/50">
          <div className="font-semibold text-zinc-600 dark:text-zinc-400">Not evaluated</div>
          <div className="mt-0.5">{capacity.notEvaluatedReason}</div>
        </div>
      ) : (
        <div className="mt-1 rounded-md bg-emerald-50 px-2 py-1.5 text-[11px] leading-relaxed text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
          The recorded reading fit the displayed capacity.
        </div>
      )}
    </div>
  );
}
